import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = { class: "text" }

import Uppy from "@uppy/core";
import DragDrop from "@uppy/vue/lib/drag-drop";
import Editor from "@tinymce/tinymce-vue";
import VErrorTooltip from "../VErrorTooltip/VErrorTooltip.vue";
import store from "@/store";
import { ref, computed, watch, reactive } from "vue";
import { getRandomId } from "@/mixins/global.mixin";
import NotifyService from "@/services/notify.service";
import { TranslateResult } from "vue-i18n";

interface Props {
  value?: string;
  label?: string | TranslateResult;
  toolbar?: string;
  allowedFileTypes?: Array<string>;
  maxFileSizeInBytes?: number;
  error?: string | TranslateResult;
  required?: boolean;
  contentCssPath?: string;
  disabled?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VInputEditor',
  props: {
    value: { default: "" },
    label: { default: "" },
    toolbar: { default: "formatselect bold italic underline strikethrough alignleft aligncenter alignright alignjustify alignnone\
          bullist numlist forecolor blockquote uploadImage link unlink table" },
    allowedFileTypes: { default: () => [".png", ".jpg", ".jpeg", ".gif"] },
    maxFileSizeInBytes: { default: 10240000 },
    error: { default: "" },
    required: { type: Boolean, default: false },
    contentCssPath: { default: "/css/tinymce-editor.css" },
    disabled: { type: Boolean, default: false }
  },
  emits: ["input", "init", "uploadedFile"],
  setup(__props: any, { emit: __emit }) {

DragDrop.compatConfig = { MODE: 3 };

Editor.compatConfig = { MODE: 3 };

const emit = __emit;

const props = __props;

const uppyDragDrop = ref();
let editorInstance = null;

const uppy = computed({
  get() {
    return new Uppy({
      restrictions: {
        allowedFileTypes: props.allowedFileTypes,
        maxNumberOfFiles: 1,
      },
    }).on("file-added", (file) => {
      if (file.size > props.maxFileSizeInBytes) {
        NotifyService.error("theFileMayNotBeGreater");

        uppy.value.removeFile(file.id);

        return;
      }

      setFiles();
    });
  },
  set() {
    // setter is canceled
  },
});

const uppyFiles = ref([]);

const config = reactive({
  files: [],
  setup: (editor) => {
    editor.on("init", () => {
      editorInstance = editor;
      emit("init");
    });

    editor.ui.registry.addButton("uploadImage", {
      icon: "image",
      onAction: () => {
        uppyDragDrop.value.$el.firstChild.firstChild.click();
      },
    });
  },
  menubar: false,
  branding: false,
  image_dimensions: false,
  resize: false,
  object_resizing: "",
  format_empty_lines: true,
  toolbar_mode: "sliding",
  autoresize_on_init: true,
  // TODO: improve TailwindCSS Typography for TinyMCE
  // init_instance_callback: function (editor) {
  //   const [head] = editor.dom.select("head");
  //
  //   editor.dom.remove(editor.dom.get("mce-u0"));
  //
  //   editor.dom.add(head, "script", {
  //     src: "https://cdn.tailwindcss.com?plugins=typography",
  //     type: "text/javascript",
  //   });
  // },
  // body_class: "prose prose-base min-w-full p-5",
  content_css: [
    //   "https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap",
    props.contentCssPath,
  ],
  plugins: [
    "lists",
    "image",
    "visualblocks",
    "paste",
    "link",
    "hr",
    "code",
    "textcolor",
    "table",
    "autoresize",
  ],
});

const body = computed({
  get() {
    return props.value;
  },
  set(html) {
    emit("input", html);
  },
});

const editorId = computed(() => getRandomId());
const uppyId = computed(() => getRandomId());

const apiKey = computed(() => process.env.VUE_APP_TINYMCE_API_KEY);

const inputWrapperClasses = computed(() => {
  const classes = {
    "input-required": props.required,
    "input-error": props.error,
  };

  return [classes];
});

const uploadFiles = async () => {
  if (uppyFiles.value.length) {
    const [file] = uppyFiles.value.map((file) => file.data);

    const image = await Promise.resolve(store.dispatch("file/uploadFile", file));

    if (image) {
      editorInstance.insertContent(
        `<img style="display: block; margin-left: auto; margin-right: auto;" src="${image.link}" alt="" />`
      );
    }

    emit("uploadedFile", image);

    uppyFiles.value = [];
    uppy.value.cancelAll();
  }
};

watch(() => uppyFiles.value, uploadFiles, { deep: true });

const setFiles = () => {
  const { files } = uppy.value.getState();

  uppyFiles.value = Object.values(files);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["editor", inputWrapperClasses.value])
  }, [
    (_ctx.label.length)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          class: "label",
          for: editorId.value
        }, [
          _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.label), 1)
        ], 8, _hoisted_1))
      : _createCommentVNode("", true),
    _createVNode(_unref(DragDrop), {
      id: uppyId.value,
      ref_key: "uppyDragDrop",
      ref: uppyDragDrop,
      uppy: uppy.value,
      class: _normalizeClass(["uppy-upload", uppyId.value])
    }, null, 8, ["id", "uppy", "class"]),
    _createVNode(_unref(Editor), {
      id: editorId.value,
      modelValue: body.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((body).value = $event)),
      "api-key": apiKey.value,
      init: config,
      toolbar: _ctx.toolbar,
      disabled: props.disabled
    }, null, 8, ["id", "modelValue", "api-key", "init", "toolbar", "disabled"]),
    (_ctx.error)
      ? (_openBlock(), _createBlock(VErrorTooltip, {
          key: 1,
          error: _ctx.error
        }, null, 8, ["error"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})